import React from 'react'
import { redirect } from 'redux-first-router'
import * as R from 'ramda'

import { DEFAULT_JOBS_QUERY_PARAMS } from 'constants/projects'
import { DEFAULT_INTERVIEW_TEMPLATES_QUERY_PARAMS } from 'constants/templates'
import { toProjects, toTemplates, toTemplatesCreator } from 'ducks/router'
import { useConfirmationModal, useSnackbar } from 'hooks'
import { useData } from './useData'
import { useDeleteCategoryModal } from './useDeleteCategoryModal'
import { useDeleteQuestionModal } from './useDeleteQuestionModal'
import { useEditNameModal } from './useEditNameModal'
import { useInternals } from './useInternals'
import { useMutations } from './useMutations'
import { useQueries } from './useQueries'

// NOTE: hook that groups up all the local hooks and
// exposes state and handlers for the interview template
// creator components
export const useInternalState = (state, dispatch) => {
  const { uuid } = state

  const handleRedirectToJobs = React.useCallback(() => {
    dispatch(redirect(toProjects(null, null, DEFAULT_JOBS_QUERY_PARAMS)))
  }, [dispatch])

  const handleRedirectToInterviewTemplates = React.useCallback(() => {
    dispatch(
      toTemplates('interviews', null, DEFAULT_INTERVIEW_TEMPLATES_QUERY_PARAMS),
    )
  }, [dispatch])

  const handleInterviewTemplateRedirect = React.useCallback(
    ({ uuid }) => {
      dispatch(toTemplatesCreator({ tab: 'interviews', uuid }))
    },
    [dispatch],
  )

  const {
    handlers: { handleShowSnackbar },
  } = useSnackbar()

  const { handleFetchTemplates } = useQueries()

  const {
    handleDeleteTemplateQuestion,
    handleDeleteTemplateSection,
    handlePatchTemplate,
    handlePatchTemplateQuestion,
    handlePatchTemplateSection,
    handlePostTemplateQuestion,
    handlePostTemplateSection,
  } = useMutations()

  const {
    state: { meta, response, isFetching },
    handlers: { handleSetResponse },
  } = useData({ uuid }, { handleFetchTemplates })

  const {
    language,
    sections = [],
    title: templateName,
    url: templateUrl,
    pdf,
    writeable,
    projects = [],
  } = response ?? {}

  const {
    state: {
      activeSection,
      activeStep,
      currentStepIndex,
      edit,
      questionsCount,
      sectionsCount,
      steps,
      isCategoryScorable,
      isCategoryDeletable,
      hasNoQuestions,
      isEditMode,
      isIdleMode,
      isNextCategoryDisabled,
      isNextStepDisabled,
      isPreviousCategoryDisabled,
      isPreviousStepDisabled,
      isStaticPage,
    },
    handlers: {
      handleChangeNextStep,
      handleChangePreviousStep,
      handleChangeStep,
      handleChangeMode,
      handleDeleteQuestion,
      handleEditCategoryCancel,
      handleEditCategoryConfirm,
      handleEditCategoryOpen,
      handleInsertCategory,
      handleInsertQuestion,
      handleUpgradeQuestion,
      watch,
    },
  } = useInternals(
    { sections, templateUrl },
    {
      handleDeleteTemplateQuestion,
      handlePatchTemplateQuestion,
      handlePatchTemplateSection,
      handlePostTemplateQuestion,
      handlePostTemplateSection,
      handleSetResponse,
      handleShowSnackbar,
    },
  )

  const {
    handleEditNameClose,
    handleEditNameConfirm,
    handleEditNameOpen,
    isEditNameOpen,
  } = useEditNameModal(
    { uuid },
    { handleSetResponse, handlePatchTemplate, handleShowSnackbar },
  )

  const {
    handleDeleteCategoryClose,
    handleDeleteCategoryConfirm,
    handleDeleteCategoryOpen,
    isDeleteCategoryOpen,
  } = useDeleteCategoryModal(
    { activeSection, currentStepIndex, steps, isEditMode },
    {
      handleChangeStep,
      handleChangeMode,
      handleDeleteTemplateSection,
      handleSetResponse,
      handleShowSnackbar,
    },
  )

  const {
    handleDeleteQuestionClose,
    handleDeleteQuestionConfirm,
    handleDeleteQuestionOpen,
    isDeleteQuestionOpen,
  } = useDeleteQuestionModal({ handleDeleteQuestion, handleUpgradeQuestion })

  const {
    handleConfirmationClose,
    handleConfirmationConfirm,
    handleConfirmationOpen,
    isConfirmationOpen,
  } = useConfirmationModal(handleRedirectToInterviewTemplates)

  if (R.equals(meta?.HTTPstatus, 404)) {
    handleRedirectToJobs()
  }

  return {
    state: {
      activeSection,
      activeStep,
      edit: { ...edit, watch },
      language,
      questionsCount,
      sectionsCount,
      sections,
      steps,
      pdf,
      templateName,
      hasNoQuestions,
      isCategoryScorable,
      isCategoryDeletable,
      isConfirmationOpen,
      isDeleteCategoryOpen,
      isDeleteQuestionOpen,
      isEditNameOpen,
      isEditMode,
      isFetching,
      isIdleMode,
      isNextCategoryDisabled,
      isNextStepDisabled,
      isPreviousCategoryDisabled,
      isPreviousStepDisabled,
      isStaticPage,
      projects,
    },
    handlers: {
      handleInterviewTemplateRedirect,
      handleChangeNextStep,
      handleChangePreviousStep,
      handleChangeStep,
      handleConfirmationClose,
      handleConfirmationConfirm,
      handleConfirmationOpen,
      handleDeleteCategoryClose,
      handleDeleteCategoryConfirm,
      handleDeleteCategoryOpen,
      handleDeleteQuestionClose,
      handleDeleteQuestionConfirm,
      handleDeleteQuestionOpen,
      handleEditCategoryCancel,
      handleEditCategoryConfirm,
      handleEditCategoryOpen,
      handleEditNameClose,
      handleEditNameConfirm,
      handleEditNameOpen,
      handleInsertCategory,
      handleInsertQuestion,
    },
  }
}
